import { ColumnDef } from "@tanstack/react-table";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Badge, { BadgeBg } from "components/base/Badge";
import StarCheckbox from "components/base/StarCheckbox";
import RevealDropdown, {
  RevealDropdownTrigger,
} from "components/base/RevealDropdown";
import ActionDropdownItems from "components/custom/ActionDropdownItems";
import { currencyFormat } from "helpers/utils";
import { ProductsTableProductType, Status, formatDateForInput } from "pages/apps/e-commerce/admin/Products";
import { ShippingMethod } from "components/modals/CommissionDetailsModal";
import StockDetailsModal from "components/modals/StockDetailsModal";
import { useContext, createContext, useEffect, useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarehouse, faEdit, faPencilAlt, faCopy } from "@fortawesome/free-solid-svg-icons";

// Create context for sharing the callback functions
export const AdvanceTableContext = createContext<{
  props?: {
    onShowReasonDetails?: (details: { reason: string[]; description: string }) => void;
    onShowCommissionDetails?: (product: ProductsTableProductType) => void;
    onShowStockDetails?: (product: ProductsTableProductType) => void;
    // Selection-based editing props
    selectedRows?: { [key: string]: ProductsTableProductType };
    editedProducts?: { [key: string]: { [field: string]: any } };
    onFieldChange?: (productId: string, field: string, value: any) => void;
    onCheckboxChange?: (productId: string, checked: boolean) => void;
    // Inline editing props
    onInlineEdit?: (action: string, data?: any) => void;
    editingCell?: string;
    editingRow?: string | null;
    editValue?: string | number;
    editSpecialPrice?: string | number;
    editStartDate?: string;
    editEndDate?: string;
  };
} | null>(null);

// Interface for props with optional callback functions
interface ProductsTableProps {
  totalCount?: number;
  onShowReasonDetails?: (details: { reason: string[]; description: string }) => void;
  onShowCommissionDetails?: (product: ProductsTableProductType) => void;
  onShowStockDetails?: (product: ProductsTableProductType) => void;
  // Selection-based editing props
  selectedRows?: { [key: string]: ProductsTableProductType };
  editedProducts?: { [key: string]: { [field: string]: any } };
  onFieldChange?: (productId: string, field: string, value: any) => void;
  onCheckboxChange?: (productId: string, checked: boolean) => void;
  // Inline editing props
  onInlineEdit?: (action: string, data?: any) => void;
  editingCell?: string;
  editingRow?: string | null;
  editValue?: string | number;
  editSpecialPrice?: string | number;
  editStartDate?: string;
  editEndDate?: string;
}

// Main component definition
const ProductsTable = ({ 
  totalCount, 
  onShowReasonDetails, 
  onShowCommissionDetails,
  onShowStockDetails,
  // Selection-based editing props
  selectedRows = {},
  editedProducts = {},
  onFieldChange,
  onCheckboxChange,
  // Old inline editing props
  onInlineEdit,
  editingCell,
  editingRow,
  editValue,
  editSpecialPrice,
  editStartDate,
  editEndDate
}: ProductsTableProps) => {
  // State for the stock modal
  const [showStockModal, setShowStockModal] = useState(false);
  const [currentStockProduct, setCurrentStockProduct] = useState<ProductsTableProductType | null>(null);

  // Handler for showing stock details in modal
  const handleShowStockDetails = (product: ProductsTableProductType) => {
    setCurrentStockProduct(product);
    setShowStockModal(true);
  };

  // Handler for closing the stock modal
  const handleCloseStockModal = () => {
    setShowStockModal(false);
  };
  const { t } = useTranslation();
  
  // Function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        // Could show a tooltip or notification here
        console.log('Copied to clipboard:', text);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  // Use useMemo to create translated column definitions
  // This ensures they're only recreated when the language changes
  const translatedColumns = useMemo<ColumnDef<ProductsTableProductType>[]>(() => [
    //  1) Product Image and Product Name
    {
      id: "product",
      accessorKey: "product",
      header: t("products.details"),
      cell: ({ row: { original } }) => {
        const { product, productImage, sku, sellerReference, type, visibility } = original;
        const navigate = useNavigate();
        return (
          <div className="d-flex align-items-center">
            {/* Product Image */}
            <img
              src={productImage}
              alt={product}
              width={53}
              height={53}
              className="me-3 border border-translucent rounded-2"
              style={{ objectFit: "contain" }}
            />

            {/* Product Information */}
            <div className="d-flex flex-column">
              <a 
                href={`/products/${original.id}`}
                className="fw-semibold line-clamp-3 text-body-highlight cursor-pointer" 
                style={{ textDecoration: 'underline' }}
                title={product}
                // target="_blank" // Allows opening in new tab when right-clicked
                rel="noopener noreferrer"
              >
                {product}
              </a>

              {/* SKU with copy button */}
              <div className="d-flex align-items-center text-body-secondary fs-10">
                <span>{t("sku")}: {sku}</span>
                <button 
                  className="btn btn-sm btn-link p-0 ms-1" 
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(sku);
                  }}
                  title={t("copy")}
                >
                  <FontAwesomeIcon icon={faCopy} size="sm" />
                </button>
              </div>

              {/* Seller Reference with copy button */}
              <div className="d-flex align-items-center text-body-highlight fs-10">
                <span>{t("sellerReference")}: {sellerReference || t("notAvailable")}</span>
                {sellerReference && (
                  <button 
                    className="btn btn-sm btn-link p-0 ms-1" 
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(sellerReference);
                    }}
                    title={t("copy")}
                  >
                    <FontAwesomeIcon icon={faCopy} size="sm" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: 350 }, className: "ps-4" },
        cellProps: { className: "ps-4" },
      },
    },
    // 2) Add a new column for the product type
    {
      id: "type",
      accessorKey: "type",
      header: t("type"),
      cell: ({ row: { original } }) => {
        const { type, visibility } = original;

        // Determine badge variant and display text based on type and visibility
        let variant: BadgeBg;
        let displayText: string;

        if (type === "configurable") {
          variant = "primary";
          displayText = t("configurable");
        } else if (type === "simple" && visibility === 1) {
          variant = "info"; // Use a distinct color for Variant
          displayText = t("variant");
        } else {
          variant = "secondary";
          displayText = t("simple");
        }

        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // 3) Add a new column for the product variant
    {
      id: "variant",
      accessorKey: "variant",
      header: t("variant"),
      cell: ({ row: { original } }) => (original.variant),
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center" },
      },
    },
    // 4) Add a new column for the product situation
    {
      id: "situation",
      accessorKey: "situation",
      header: t("situation"),
      cell: ({ row: { original } }) => {
        const { status } = original;
        const displayText = status === 1 ? t("onSale") : t("closeForSale");
        const variant: BadgeBg = status === 1 ? "success" : "danger";

        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // 5) Add a new column for the product %commission
    {
      id: "percentCommission",
      accessorKey: "percentCommission",
      header: t("products.percentCommission"),
      cell: ({ row: { original } }) => {
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowCommissionDetails function from the context
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;

        return (
          <span 
            className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline" : ""}
            onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
          >
            {original.percentCommission} %
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 6) Add a new column for the product fix commission
    {
      id: "fixCommission",
      accessorKey: "fixCommission",
      header: t("products.fixCommission"),
      cell: ({ row: { original } }) => {
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowCommissionDetails function from the context
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;

        return (
          <span 
            className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline" : ""}
            onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
          >
            {currencyFormat(original.fixCommission ?? 0)}
          </span>
        );
      },
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 7) Add a new column for the product price
    {
      id: "Price",
      accessorKey: "Price",
      header: t("price"),
      cell: ({ row: { original } }) => {
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        
        // Extract context values
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;
        const onInlineEdit = context?.props?.onInlineEdit;
        const isEditing = context?.props?.editingCell === `price-${original.id}`;
        const editValue = context?.props?.editValue;
        
        // If in editing mode
        if (isEditing && onInlineEdit) {
          return (
            <div className="d-flex flex-column align-items-center">
              <input
                type="number"
                className="form-control form-control-sm text-center mb-2"
                style={{ width: "80px" }}
                value={editValue || ""}
                onChange={(e) => onInlineEdit("editValue", e.target.value)}
                min="0"
                step="0.01"
                autoFocus
              />
              
              {/* Save/Cancel buttons in a row */}
              <div className="d-flex justify-content-center gap-2">
                <button 
                  className="btn btn-sm btn-success"
                  onClick={() => onInlineEdit("saveEdit", { 
                    field: "price", 
                    id: original.id, 
                    value: editValue 
                  })}
                >
                  <i className="fas fa-check"></i>
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => onInlineEdit("cancelEdit")}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          );
        }

        // If this row is being edited, show nothing (as the edit UI is in the special price column)
        if (context?.props?.editingRow === original.id && onInlineEdit) {
          return null;
        }

        // Regular display mode
        return (
          <div className="d-flex align-items-center justify-content-center">
            <span 
              className={onShowCommissionDetails ? "cursor-pointer text-decoration-underline me-2" : "me-2"}
              onClick={() => onShowCommissionDetails && onShowCommissionDetails(original)}
            >
              {currencyFormat(original.price)}
            </span>
            {onInlineEdit && context?.props?.editingRow !== original.id && (
              <button 
                className="btn btn-sm secondary "
                onClick={() => onInlineEdit("startRowEdit", {
                  id: original.id
                })}
              >
                <FontAwesomeIcon icon={faPencilAlt} />
              </button>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4 " },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 8) Add a new column for the product special price
    {
      id: "specialPrice",
      accessorKey: "specialPrice",
      header: t("specialPrice"),
      cell: ({ row: { original } }) => {
        const { specialPrice, discountStartDate, discountEndDate } = original;

        // ✅ Ensure specialPrice is always a number
        const price = specialPrice ?? 0;

        // ✅ Format date function (fix TypeScript issue)
        const formatDate = (date: string | null | undefined): string =>
          date ? new Date(date).toLocaleDateString("fr-FR") : t("notAvailable");
          
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        
        // Extract context values
        const onShowCommissionDetails = context?.props?.onShowCommissionDetails;
        const onInlineEdit = context?.props?.onInlineEdit;
        const isEditingPrice = context?.props?.editingCell === `specialPrice-${original.id}`;
        const isEditingStartDate = context?.props?.editingCell === `startDate-${original.id}`;
        const isEditingEndDate = context?.props?.editingCell === `endDate-${original.id}`;
        const isEditingRow = context?.props?.editingRow === original.id;
        const editValue = context?.props?.editValue;
        const editSpecialPrice = context?.props?.editSpecialPrice;
        const editStartDate = context?.props?.editStartDate;
        const editEndDate = context?.props?.editEndDate;
        
        // If in row editing mode (for both price and special price)
        if (isEditingRow && onInlineEdit) {
          // Create a combined editor for price + dates
          return (
            <div className="d-flex flex-column align-items-center gap-2">
              {/* Price input */}
              <div className="mb-2">
                <label className="form-label mb-1 text-center small">{t("price")}</label>
                <input
                  type="number"
                  className="form-control form-control-sm text-center"
                  style={{ width: "100px" }}
                  value={editValue || ""}
                  onChange={(e) => onInlineEdit("editValue", e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              
              {/* Special price input */}
              <div className="mb-2">
                <label className="form-label mb-1 small">{t("specialPrice")}</label>
                <input
                  type="number"
                  className="form-control form-control-sm text-center"
                  style={{ width: "100px" }}
                  value={editSpecialPrice || ""}
                  onChange={(e) => onInlineEdit("editSpecialPrice", e.target.value)}
                  min="0"
                  step="0.01"
                />
              </div>
              
              {/* Date range inputs */}
              <div className="mb-2">
                <label className="form-label mb-1 small">{t("inventoryHistory.fromDate")}</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={editStartDate || formatDateForInput(discountStartDate)}
                  onChange={(e) => onInlineEdit("editStartDate", e.target.value)}
                />
              </div>

              <div className="mb-2">
                <label className="form-label mb-1 small">{t("inventoryHistory.toDate")}</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={editEndDate || formatDateForInput(discountEndDate)}
                  onChange={(e) => onInlineEdit("editEndDate", e.target.value)}
                />
              </div>
              
              {/* Save/Cancel buttons in a row */}
              <div className="d-flex justify-content-center gap-2 mt-1">
                <button 
                  className="btn btn-sm btn-success"
                  onClick={() => {
                    // Save all price fields at once
                    onInlineEdit("saveEdit", { 
                      field: "rowPricing", 
                      id: original.id, 
                      price: editValue,
                      specialPrice: editSpecialPrice,
                      startDate: editStartDate,
                      endDate: editEndDate
                    });
                  }}
                >
                  <i className="fas fa-check"></i> Save
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => onInlineEdit("cancelEdit")}
                >
                  <i className="fas fa-times"></i> Cancel
                </button>
              </div>
            </div>
          );
        }

        // This block was removed to eliminate duplication - combined editing is handled above
        
        // If in edit mode for start date
        if (isEditingStartDate && onInlineEdit) {
          return (
            <div className="d-flex flex-column align-items-center">
              <input
                type="date"
                className="form-control form-control-sm text-center mb-2"
                value={editStartDate || ""}
                onChange={(e) => onInlineEdit("editStartDate", e.target.value)}
                autoFocus
                onBlur={() => onInlineEdit("saveEdit", { 
                  field: "discountStartDate", 
                  id: original.id, 
                  value: editStartDate 
                })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onInlineEdit("saveEdit", { 
                      field: "discountStartDate", 
                      id: original.id, 
                      value: editStartDate 
                    });
                  } else if (e.key === "Escape") {
                    onInlineEdit("cancelEdit");
                  }
                }}
              />
              <div className="d-flex justify-content-center gap-2">
                <button 
                  className="btn btn-sm btn-success"
                  onClick={() => onInlineEdit("saveEdit", { 
                    field: "discountStartDate", 
                    id: original.id, 
                    value: editStartDate 
                  })}
                >
                  <i className="fas fa-check"></i>
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => onInlineEdit("cancelEdit")}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          );
        }
        
        // If in edit mode for end date
        if (isEditingEndDate && onInlineEdit) {
          return (
            <div className="d-flex flex-column align-items-center">
              <input
                type="date"
                className="form-control form-control-sm text-center mb-2"
                value={editEndDate || ""}
                onChange={(e) => onInlineEdit("editEndDate", e.target.value)}
                autoFocus
                onBlur={() => onInlineEdit("saveEdit", { 
                  field: "discountEndDate", 
                  id: original.id, 
                  value: editEndDate 
                })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onInlineEdit("saveEdit", { 
                      field: "discountEndDate", 
                      id: original.id, 
                      value: editEndDate 
                    });
                  } else if (e.key === "Escape") {
                    onInlineEdit("cancelEdit");
                  }
                }}
              />
              <div className="d-flex justify-content-center gap-2">
                <button 
                  className="btn btn-sm btn-success"
                  onClick={() => onInlineEdit("saveEdit", { 
                    field: "discountEndDate", 
                    id: original.id, 
                    value: editEndDate 
                  })}
                >
                  <i className="fas fa-check"></i>
                </button>
                <button 
                  className="btn btn-sm btn-danger"
                  onClick={() => onInlineEdit("cancelEdit")}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          );
        }

        // Regular display mode
        return (
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex align-items-center mb-1">
              <span 
                className={`fw-semibold text-body-highlight ${onShowCommissionDetails && price > 0 ? "text-decoration-underline me-2" : "me-2"}`}
                onClick={() => price > 0 && onShowCommissionDetails && onShowCommissionDetails(original)}
              >
                {price > 0 ? currencyFormat(price, { currency: "TND" }) : "-"}
              </span>
              {/* Edit button already shown in the price column */}
            </div>

            {/* Date range row */}
            {(price > 0 || onInlineEdit) && (
              <div className="d-flex flex-column">
                {/* Start date row */}
                <div className="d-flex align-items-center justify-content-center">
                  <span className="text-body-secondary fs-10 me-1">
                    {t("products.from")}:
                  </span>
                  <span className="text-body-secondary fs-10">
                    {formatDate(discountStartDate)}
                  </span>
                </div>
                
                {/* End date row */}
                <div className="d-flex align-items-center justify-content-center">
                  <span className="text-body-tertiary fs-10 me-1">
                    {t("products.to")}:
                  </span>
                  <span className="text-body-tertiary fs-10">
                    {formatDate(discountEndDate)}
                  </span>
                </div>
              </div>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4" },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center " },
      },
    },
    // 9) Add a new column for the product shipping method
    {
      id: "shippingMethod",
      accessorKey: "shippingMethod",
      header: t("products.shippingMethod"),
      cell: ({ row: { original } }) => {
        const { shippingMethod } = original;
        let displayText = "";
        let variant: BadgeBg = "secondary";
        if (shippingMethod === ShippingMethod.FBW) {
          displayText = t("shippingMethodType.fbw");
          variant = "success";
        } else if (shippingMethod === ShippingMethod.ESS) {
          displayText = t("shippingMethodType.ess");
          variant = "warning";
        } else if (shippingMethod === ShippingMethod.ESW) {
          displayText = t("shippingMethodType.esw");
          variant = "info";
        }
        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 200 }, className: "ps-4" },
        cellProps: { className: "fs-9 fw-semibold ps-4 text-body-highlight text-opacity-85", },
      },
    },
    // 10) Add a new column for the product stock
    {
      id: "stock",
      accessorKey: "stock",
      header: t("stock"),
      cell: ({ row: { original } }) => {
        const { stock, isInStock, salable_qty,shippingMethod } = original;
        
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowStockDetails function from the context or use the internal handler
        const onShowStockDetails = context?.props?.onShowStockDetails || handleShowStockDetails;
        
        // Create stock object from product data
        const stockData = {
          qty: stock,
          is_in_stock: isInStock,
          salable_qty: salable_qty
        };
        
        const stockColor = stockData.qty > 0 ? 
          (stockData.salable_qty !== undefined && stockData.salable_qty <= 0 ? 'warning' : 'success') : 
          'danger';
        
        return (
          <div 
            className="d-flex align-items-center justify-content-center cursor-pointer"
            onClick={() => onShowStockDetails(original)}
          >
            <Badge 
              bg={stockColor} 
              variant="phoenix" 
              className="fs-10 px-2 py-1 stock-badge"
              style={{
                transition: "all 0.2s ease-in-out",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.boxShadow = "0 2px 5px rgba(0,0,0,0.2)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {stock}
            </Badge>
            {/* <FontAwesomeIcon icon={faWarehouse} className="text-secondary ms-2" /> */}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: 100 }, className: "ps-4" },
        cellProps: { className: "fw-semibold ps-4 text-body-highlight text-center" },
      },
    },
    // 13) Add a new column for the product brand
    {
      id: "brand",
      accessorKey: "brand",
      header: t("brand"),
      cell: ({ row: { original } }) => original.brand === "Pas fournie" ? "-" : original.brand,
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4" },
        cellProps: {
          className: "fs-9 fw-semibold ps-4 text-body-highlight text-opacity-85",
        },
      },
    },
    // 14) Add a new column for the product category
    {
      id: "category",
      accessorKey: "category",
      header: t("category"),
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
      cell: ({ row: { original } }) => {
        const { category } = original;

        return category?.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {category.map((cat: any, index) => (
              <Badge key={index} bg="secondary" variant="phoenix">
                {cat.name}
              </Badge>
            ))}
          </div>
        ) : (
          t("notAvailable")
        );
      },
    },
    // Add a new column for deactivation reasons
    {
      id: "deactivation_reason",
      accessorKey: "deactivation_reason",
      header: t("products.deactivationReasons"),
      meta: {
        headerProps: { style: { width: 180 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
      cell: ({ row: { original } }) => {
        const { deactivation_reason } = original;
        
        // Get the global table context
        const context = useContext(AdvanceTableContext);
        // Extract the onShowReasonDetails function from the context
        const onShowReasonDetails = context?.props?.onShowReasonDetails;

        const handleReasonsClick = () => {
          if (deactivation_reason && onShowReasonDetails) {
            onShowReasonDetails(deactivation_reason);
          }
        };

        // Check if deactivation_reason exists and has reasons array
        return deactivation_reason && deactivation_reason.reason && deactivation_reason.reason.length > 0 ? (
          <div className="d-flex flex-wrap gap-1">
            {deactivation_reason.reason.map((reasonText: string, index: number) => (
              <Badge 
                key={index} 
                bg="danger" 
                variant="phoenix"
                className="cursor-pointer"
                onClick={handleReasonsClick}
              >
                {reasonText}
              </Badge>
            ))}
          </div>
        ) : (
          "-"
        );
      },
    },
    // Add a new column for the product weight
    {
      id: "weight",
      accessorKey: "weight",
      header: t("weight"),
      cell: ({ row: { original } }) => (
        <span className="text-body-secondary fw-semibold">
          {original.weight !== "N/A" ? `${original.weight} kg` : t("notAvailable")}
        </span>
      ),
      meta: {
        headerProps: { style: { width: 120 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // Add a new column for the product approval status
    {
      id: "product_status",
      accessorKey: "product_status",
      header: t("products.approvalStatus"),
      cell: ({ row: { original } }) => {
        const { product_status } = original;

        const displayText =
          product_status === 1 ? t("approved") : product_status === 3 ? t("rejected") : t("disapproved");
        const variant: BadgeBg =
          product_status === 1 ? "success" : product_status === 3 ? "danger" : "warning";

        return <Badge bg={variant} variant="phoenix">{displayText}</Badge>;
      },
      meta: {
        headerProps: { style: { width: 150 }, className: "ps-4" },
        cellProps: { className: "ps-4 text-center" },
      },
    },
    // Add a new column for the product action 
    {
      id: "action",
      cell: ({ row: { original } }) => ( // Access row data here
        <RevealDropdownTrigger>
          <RevealDropdown>
            <ActionDropdownItems productId={original.id} /> {/* Pass product ID correctly */}
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { style: { width: "7%" } },
        cellProps: { className: "text-end" },
      },
    }
  ], [t]); // Add t to dependency array to update when language changes

  // Update the exported columns for use in Products.tsx
  useEffect(() => {
    // Update the exported columns
    productsTablecolumns.length = 0;
    productsTablecolumns.push(...translatedColumns);
  }, [translatedColumns]);

  return (
    <AdvanceTableContext.Provider value={{ 
      props: { 
        onShowReasonDetails, 
        onShowCommissionDetails, 
        onShowStockDetails,
        // Selection-based editing props
        selectedRows,
        editedProducts,
        onFieldChange,
        onCheckboxChange,
        // Old inline editing props
        onInlineEdit,
        editingCell,
        editingRow,
        editValue,
        editSpecialPrice,
        editStartDate,
        editEndDate
      } 
    }}>
      {/* Stock Details Modal */}
      {currentStockProduct && (
        <StockDetailsModal
          show={showStockModal}
          onHide={handleCloseStockModal}
          productName={currentStockProduct.product}
          productSku={currentStockProduct.sku}
          stock={{
            qty: currentStockProduct.stock,
            is_in_stock: currentStockProduct.isInStock,
            salable_qty: currentStockProduct.salable_qty
          }}
        />
      )}
      <div>
        {/* The table - don't pass columns directly */}
        <AdvanceTable 
          tableProps={{ className: "phoenix-table fs-9" }}
        />
        {/* Pagination */}
        <AdvanceTableFooter 
          pagination 
          totalCount={totalCount}
          rowsPerPageOptions={[10, 25, 50, 100]}
          showViewAllBtn={false} // Explicitly hide the View All button
        />
      </div>
    </AdvanceTableContext.Provider>
  );
};

// Export the component
export default ProductsTable;

// Export the columns for use in the main Products component
export const productsTablecolumns: ColumnDef<ProductsTableProductType>[] = [];
